import React from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'wouter';
import { Icon } from '../../../../concepts/resources/Icon';
import { Colors } from '../../../../ui';
import { Shadows } from '../../../../ui/Standards';
import { useLocationScope } from '../../../AppLocation';
import { EnableIfHasFeature, HideIfHasFeature } from '../../../common/FeatureElements';
import { Copy, Flexer, Title } from '../../../dashboard/elements/LayoutElements';
import { ManageFrame } from '../ManageFrame';
import { SetupAzureSingleSignOnView } from './providers/SetupAzureSingleSignOnView';
import { SetupGoogleSingleSignOnView } from './providers/SetupGoogleSingleSignOnView';
import { SetupOktaSingleSignOnView } from './providers/SetupOktaSingleSignOnView';
import { SetupOtherSingleSignOnView } from './providers/SetupOtherSingleSignOnView';
import { SingleSignOnPremiumCallout } from './SingleSignOnPremiumCallout';

const Card = styled.div`
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem;
    box-shadow: ${Shadows.md};
    border-radius: .25rem;
    border-left: .25rem solid ${Colors.primary};
    background: white;
    overflow: hidden;
    cursor: pointer;

    &:hover {
        box-shadow: ${Shadows.lg};
    }
`;

const CardLayout = styled.div`
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 2rem;
    max-width: 54rem;
`;

const CardOption = ({ title, description, to }: { title: string, description: string, to: string }) => (
    <Link to={to}>
        <Card>
            <Flexer flex="row" justify="space-between">
                <Title flush color={Colors.text}>{title}</Title>
                <Icon src="deqo://icons/hero/arrow-right" size="1.5" color={Colors.primary} />
            </Flexer>
            <Copy flush>{description}</Copy>
        </Card>
    </Link>
);

const SetupSingleSignOnProviderMenu = React.memo(() =>
    <Flexer flex="column" gap={2} maxWidth="54">
        <Copy flush>
            Metro Retro supports Single Sign-On (SSO) via the SAML protocol. You will need the SAML Endpoint, Issuer and Certificate values from your Identity Provider in order to configure Metro Retro SSO. You will also need to configure the email domains you would like to map for this SSO configuration. When signing-in, users presenting an email address from one of these domains will be redirected to your Identity Provider for authentication.
        </Copy>
        <Title size="lg" flush color={Colors.text}>
            Please choose a provider to begin configuring Single Sign-On (SSO)
        </Title>
        <Flexer flex="column" gap={2}>
            <CardLayout>
                <CardOption
                    title="Google"
                    description="Configure Google Workspace SSO integration"
                    to="~/manage/sso/setup/google"
                />
                <CardOption
                    title="Okta"
                    description="Configure Okta SSO integration"
                    to="~/manage/sso/setup/okta"
                />
                <CardOption
                    title="Azure"
                    description="Configure Azure AD SSO integration"
                    to="~/manage/sso/setup/azure"
                />
                <CardOption
                    title="Other"
                    description="Configure custom SSO integration"
                    to="~/manage/sso/setup/other"
                />
            </CardLayout>
        </Flexer>
    </Flexer>
);

export const SetupSingleSignOnView = React.memo(() => {
    useLocationScope('sso', 'overview');

    const { provider } = useParams<{ provider?: string }>();

    return <ManageFrame title="Setup Single Sign-On">
        <HideIfHasFeature name="singleSignOn">
            <SingleSignOnPremiumCallout />
            <Flexer height="2" />
        </HideIfHasFeature>
        <EnableIfHasFeature name="singleSignOn">
            {provider === 'google' && <SetupGoogleSingleSignOnView />}
            {provider === 'okta' && <SetupOktaSingleSignOnView />}
            {provider === 'azure' && <SetupAzureSingleSignOnView />}
            {provider === 'other' && <SetupOtherSingleSignOnView />}
            {!provider && <SetupSingleSignOnProviderMenu />}
        </EnableIfHasFeature>
    </ManageFrame>;
});
